/**
 * Marketing > Campaigns: the campaign list, and the entry point to everything
 * else in the feature.
 *
 * Leads with a setup banner because every prerequisite for sending — a connected
 * ActiveCampaign account, an authenticated sending domain, the postal address
 * CAN-SPAM requires in the footer, SMS credits — is account configuration rather
 * than code, and each one otherwise fails at a different and unhelpful moment.
 * A missing postal address, for instance, surfaces as a rejected campaign create
 * halfway through a send.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Modal from '../Modal';
import { SortableTh } from '../SortableTh';
import { useTableSortState } from '../../hooks/useTableSort';
import { sortRows } from '../../utils/tableSort';
import CampaignComposer from './CampaignComposer';
import CampaignReportModal from './CampaignReportModal';
import ContactsSection from './ContactsSection';
import {
  CAMPAIGN_STATUS_LABELS,
  campaignRates,
  createCampaign,
  defaultAudienceFilter,
  deleteCampaign,
  duplicateCampaign,
  fetchCampaignOptions,
  fetchCampaignReadiness,
  fetchCampaigns,
  type CampaignChannel,
  type CampaignOptions,
  type CampaignReadiness,
  type CampaignRow,
  type CampaignStatus,
} from '../../api/campaigns';

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const rate = (value: number | null): string => (value == null ? '—' : `${value}%`);

interface Props {
  userId: number;
}

const CampaignsSection: React.FC<Props> = ({ userId }) => {
  const [campaigns, setCampaigns] = useState<CampaignRow[]>([]);
  const [options, setOptions] = useState<CampaignOptions | null>(null);
  const [readiness, setReadiness] = useState<CampaignReadiness | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'All' | CampaignStatus>('All');

  const [composing, setComposing] = useState<CampaignRow | null>(null);
  const [reporting, setReporting] = useState<CampaignRow | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newChannel, setNewChannel] = useState<CampaignChannel>('email');
  const [creating, setCreating] = useState(false);
  const [tab, setTab] = useState<'campaigns' | 'contacts'>('campaigns');

  const { sortKey, sortDir, toggleSort } = useTableSortState();

  const load = useCallback(async () => {
    setError('');
    try {
      setCampaigns(await fetchCampaigns(userId));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load campaigns.');
    }
  }, [userId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      // Setup state and options are fetched alongside the list but must not be
      // able to blank the page: a campaign history is still worth reading when
      // ActiveCampaign is unreachable.
      const [list, opts, ready] = await Promise.allSettled([
        fetchCampaigns(userId),
        fetchCampaignOptions(userId),
        fetchCampaignReadiness(userId),
      ]);
      if (cancelled) return;
      if (list.status === 'fulfilled') setCampaigns(list.value);
      else setError(list.reason instanceof Error ? list.reason.message : 'Failed to load campaigns.');
      if (opts.status === 'fulfilled') setOptions(opts.value);
      if (ready.status === 'fulfilled') setReadiness(ready.value);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  // A send runs in the background on the server, one request per contact, so the
  // row is polled until it settles rather than left showing a stale count.
  const sending = campaigns.some((c) => c.status === 'sending');
  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (!sending) {
      if (pollTimer.current) clearInterval(pollTimer.current);
      pollTimer.current = null;
      return;
    }
    pollTimer.current = setInterval(() => void load(), 3000);
    return () => {
      if (pollTimer.current) clearInterval(pollTimer.current);
      pollTimer.current = null;
    };
  }, [sending, load]);

  const upsert = useCallback((row: CampaignRow) => {
    setCampaigns((list) => {
      const index = list.findIndex((c) => c.id === row.id);
      if (index === -1) return [row, ...list];
      const next = [...list];
      next[index] = row;
      return next;
    });
  }, []);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return campaigns.filter((c) => {
      if (statusFilter !== 'All' && c.status !== statusFilter) return false;
      return !term || `${c.name} ${c.subject}`.toLowerCase().includes(term);
    });
  }, [campaigns, search, statusFilter]);

  const sorted = useMemo(
    () =>
      sortRows(visible, sortKey, sortDir, (c, key) => {
        switch (key) {
          case 'name':
            return c.name;
          case 'channel':
            return c.channel;
          case 'status':
            return c.status;
          case 'audience':
            return c.audience_count;
          case 'opens':
            return campaignRates(c.stats).openRate ?? -1;
          case 'clicks':
            return campaignRates(c.stats).clickRate ?? -1;
          case 'sent':
            return c.sent_at ?? '';
          default:
            return '';
        }
      }),
    [visible, sortKey, sortDir]
  );

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    setError('');
    try {
      const created = await createCampaign(userId, {
        name: newName.trim(),
        channel: newChannel,
        filter: defaultAudienceFilter(),
      });
      upsert(created);
      setCreateOpen(false);
      setNewName('');
      setComposing(created);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create the campaign.');
    } finally {
      setCreating(false);
    }
  };

  const handleDuplicate = async (id: number) => {
    try {
      upsert(await duplicateCampaign(id, userId));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to duplicate the campaign.');
    }
  };

  const handleDelete = async (campaign: CampaignRow) => {
    if (!window.confirm(`Delete "${campaign.name}"? This cannot be undone.`)) return;
    try {
      await deleteCampaign(campaign.id, userId);
      setCampaigns((list) => list.filter((c) => c.id !== campaign.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete the campaign.');
    }
  };

  const openRow = (campaign: CampaignRow) => {
    // A draft is something to finish writing; anything past that is something to
    // read the results of.
    if (campaign.status === 'draft' || campaign.status === 'failed') {
      if (options) setComposing(campaign);
    } else {
      setReporting(campaign);
    }
  };

  if (composing && options) {
    return (
      <CampaignComposer
        campaign={composing}
        userId={userId}
        options={options}
        onClose={() => setComposing(null)}
        onSaved={(row) => {
          upsert(row);
          setComposing((current) => (current && current.id === row.id ? row : current));
        }}
      />
    );
  }

  const tabs = (
    <div className="campaign-panel-tabs" role="tablist" aria-label="Campaigns">
      {(
        [
          ['campaigns', 'Campaigns'],
          ['contacts', 'Contacts & opt-outs'],
        ] as const
      ).map(([id, label]) => (
        <button
          key={id}
          type="button"
          role="tab"
          aria-selected={tab === id}
          className={`campaign-panel-tab${tab === id ? ' campaign-panel-tab--active' : ''}`}
          onClick={() => setTab(id)}
        >
          {label}
        </button>
      ))}
    </div>
  );

  if (tab === 'contacts') {
    return (
      <section className="events-table-section" aria-labelledby="campaigns-heading">
        <h2 className="events-section-title" id="campaigns-heading">
          Campaigns
        </h2>
        {tabs}
        <ContactsSection userId={userId} />
      </section>
    );
  }

  return (
    <section className="events-table-section" aria-labelledby="campaigns-heading">
      <h2 className="events-section-title" id="campaigns-heading">
        Campaigns <span className="events-section-count">({campaigns.length})</span>
      </h2>
      {tabs}
      <p className="page-subtitle website-lead-intro">
        Email and text broadcasts, sent through ActiveCampaign. An audience can be an
        ActiveCampaign list, portal records, or both, and the results come back here.
      </p>

      {readiness && readiness.blockers.length > 0 ? (
        <div className="campaign-setup-banner" role="status">
          <strong>Before a campaign can send:</strong>
          <ul>
            {readiness.blockers.map((b) => (
              <li key={b}>{b}</li>
            ))}
          </ul>
          <p className="campaign-hint">
            Drafting and proofing work regardless — only the send is blocked.
          </p>
        </div>
      ) : null}

      {readiness?.configured &&
      readiness.optOuts &&
      (!readiness.optOuts.webhookRegistered || readiness.optOuts.lastPullError) ? (
        <div className="campaign-setup-banner" role="status">
          <strong>Unsubscribe sync needs attention:</strong>
          <ul>
            {!readiness.optOuts.webhookRegistered ? (
              <li>
                The ActiveCampaign webhook is not connected, so unsubscribes reach the portal only
                when the server reads them from ActiveCampaign, every few hours. Set
                ACTIVECAMPAIGN_WEBHOOK_SECRET on the server, with APP_URL on a public https
                address, to connect it.
              </li>
            ) : null}
            {readiness.optOuts.lastPullError ? (
              <li>
                The last read of ActiveCampaign&apos;s unsubscribes failed:{' '}
                {readiness.optOuts.lastPullError}
              </li>
            ) : null}
          </ul>
          <p className="campaign-hint">
            {readiness.optOuts.suppressedCount} address
            {readiness.optOuts.suppressedCount === 1 ? ' is' : 'es are'} currently left out of
            every campaign.
          </p>
        </div>
      ) : null}

      <div className="website-lead-controls">
        <input
          type="search"
          className="website-lead-search"
          placeholder="Search campaigns…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search campaigns"
        />
        <div className="website-lead-filters" role="group" aria-label="Filter by status">
          {(['All', 'draft', 'scheduled', 'sent', 'failed'] as const).map((s) => (
            <button
              key={s}
              type="button"
              className={`website-lead-filter${statusFilter === s ? ' website-lead-filter--active' : ''}`}
              onClick={() => setStatusFilter(s)}
            >
              {s === 'All' ? 'All' : CAMPAIGN_STATUS_LABELS[s]}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="primary-button campaign-new-button"
          onClick={() => setCreateOpen(true)}
        >
          New campaign
        </button>
      </div>

      {error ? (
        <p className="create-order-error" role="alert">
          {error}
        </p>
      ) : null}

      {loading ? (
        <p className="page-subtitle">Loading campaigns…</p>
      ) : (
        <div className="events-table-wrap">
          <table className="tasks-search-results-table website-lead-table">
            <thead>
              <tr>
                <SortableTh label="Campaign" columnKey="name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortableTh label="Channel" columnKey="channel" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortableTh label="Status" columnKey="status" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortableTh label="Recipients" columnKey="audience" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortableTh label="Opened" columnKey="opens" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortableTh label="Clicked" columnKey="clicks" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortableTh label="Sent" columnKey="sent" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 ? (
                <tr>
                  <td colSpan={8} className="events-empty-cell">
                    {campaigns.length === 0
                      ? 'No campaigns yet. Create one to get started.'
                      : 'No campaigns match this filter.'}
                  </td>
                </tr>
              ) : (
                sorted.map((c) => {
                  const rates = campaignRates(c.stats);
                  return (
                    <tr
                      key={c.id}
                      className="events-table-row-clickable"
                      onClick={() => openRow(c)}
                    >
                      <td data-label="Campaign">
                        <span className="website-lead-link">{c.name}</span>
                        {c.subject ? (
                          <span className="website-lead-muted"> — {c.subject}</span>
                        ) : null}
                        {c.status === 'failed' && c.last_error ? (
                          <span className="campaign-row-error"> {c.last_error}</span>
                        ) : null}
                      </td>
                      <td data-label="Channel">{c.channel === 'sms' ? 'SMS' : 'Email'}</td>
                      <td data-label="Status">
                        <span className={`campaign-status campaign-status--${c.status}`}>
                          {CAMPAIGN_STATUS_LABELS[c.status]}
                        </span>
                        {c.status === 'sending' ? (
                          <span className="campaign-progress">
                            {' '}
                            {c.send_progress}/{c.audience_count || '?'}
                          </span>
                        ) : null}
                      </td>
                      <td data-label="Recipients">{c.audience_count || '—'}</td>
                      <td data-label="Opened">{rate(rates.openRate)}</td>
                      <td data-label="Clicked">{rate(rates.clickRate)}</td>
                      <td data-label="Sent">{formatDateTime(c.sent_at)}</td>
                      <td data-label="Actions" className="campaign-row-actions" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="link-button"
                          onClick={() => void handleDuplicate(c.id)}
                        >
                          Duplicate
                        </button>
                        {c.status !== 'sending' ? (
                          <button
                            type="button"
                            className="link-button link-button--danger"
                            onClick={() => void handleDelete(c)}
                          >
                            Delete
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {createOpen ? (
        <Modal isOpen onClose={() => setCreateOpen(false)} title="New campaign" narrow>
          <div className="modal-body">
            <label className="form-label" htmlFor="new-campaign-name">
              Campaign name
            </label>
            <input
              id="new-campaign-name"
              className="form-input"
              value={newName}
              maxLength={200}
              autoFocus
              onChange={(e) => setNewName(e.target.value)}
            />
            <label className="form-label" htmlFor="new-campaign-channel">
              Channel
            </label>
            <select
              id="new-campaign-channel"
              className="form-input"
              value={newChannel}
              onChange={(e) => setNewChannel(e.target.value as CampaignChannel)}
            >
              <option value="email">Email</option>
              <option value="sms">SMS</option>
            </select>
          </div>
          <div className="modal-actions">
            <button type="button" className="secondary-button" onClick={() => setCreateOpen(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="primary-button"
              disabled={creating || !newName.trim()}
              onClick={handleCreate}
            >
              {creating ? 'Creating…' : 'Create and edit'}
            </button>
          </div>
        </Modal>
      ) : null}

      {reporting ? (
        <CampaignReportModal
          campaign={reporting}
          userId={userId}
          onClose={() => setReporting(null)}
          onUpdated={upsert}
        />
      ) : null}
    </section>
  );
};

export default CampaignsSection;
